import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import Icon from '../../components/AppIcon';
import { useToast } from '../../context/ToastContext';
import { fetchPayouts, finalizePayoutWithOtp } from '../../lib/adminApi';
import { exportToCsv, downloadFilename } from '../../lib/csvExport';

const statusFilters = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'processing', label: 'Processing' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
];

const statusStyles = {
  pending: 'bg-amber-100 text-amber-700',
  processing: 'bg-sky-100 text-sky-700',
  completed: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-rose-100 text-rose-700',
  success: 'bg-emerald-100 text-emerald-700',
};

export default function PayoutsPage() {
  const toast = useToast();
  const [statusFilter, setStatusFilter] = useState('all');
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [otpByPayout, setOtpByPayout] = useState({});
  const [finalizingId, setFinalizingId] = useState(null);

  const loadPayouts = () => {
    setLoading(true);
    setError(null);
    fetchPayouts({ status: statusFilter === 'all' ? undefined : statusFilter })
      .then((data) => setPayouts(data))
      .catch((e) => setError(e?.message || 'Failed to load payouts'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadPayouts();
  }, [statusFilter]);

  const handleFinalizeOtp = async (payoutId) => {
    const otp = otpByPayout[payoutId]?.trim();
    if (!otp || otp.length < 4) {
      toast.error('Enter the 6-digit OTP from the Paystack email.');
      return;
    }
    setFinalizingId(payoutId);
    try {
      await finalizePayoutWithOtp(payoutId, otp);
      toast.success('Transfer completed.');
      setOtpByPayout((prev) => ({ ...prev, [payoutId]: '' }));
      loadPayouts();
    } catch (e) {
      toast.error(e?.message || 'Failed to complete transfer.');
    } finally {
      setFinalizingId(null);
    }
  };

  const handleExport = () => {
    const rows = payouts.map((p) => ({
      id: p.id,
      amount: p.amount,
      status: p.status,
      payout_method: p.payout_method ?? '',
      transfer_code: p.transfer_code ?? '',
      caterer_name: p.caterer?.business_name ?? '',
      caterer_email: p.caterer?.email ?? '',
      requested_date: p.requested_date ? format(new Date(p.requested_date), 'yyyy-MM-dd HH:mm') : '',
      completed_date: (p.completed_at || p.completed_date) ? format(new Date(p.completed_at || p.completed_date), 'yyyy-MM-dd HH:mm') : '',
      failure_reason: (p.failed_reason ?? p.failure_reason) ?? '',
    }));
    if (!rows.length) {
      toast.info('No payouts to export');
      return;
    }
    exportToCsv(rows, [
      { key: 'id', label: 'ID' },
      { key: 'amount', label: 'Amount' },
      { key: 'status', label: 'Status' },
      { key: 'payout_method', label: 'Method' },
      { key: 'transfer_code', label: 'Transfer code' },
      { key: 'caterer_name', label: 'Caterer' },
      { key: 'caterer_email', label: 'Caterer email' },
      { key: 'requested_date', label: 'Requested' },
      { key: 'completed_date', label: 'Completed' },
      { key: 'failure_reason', label: 'Failure reason' },
    ], downloadFilename('payouts'));
    toast.success(`Exported ${rows.length} payouts`);
  };

  const totalAmount = payouts.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const completedCount = payouts.filter((p) => p.status === 'completed' || p.status === 'success').length;
  const failedCount = payouts.filter((p) => p.status === 'failed').length;

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Payouts</h2>
          <p className="text-sm text-muted-foreground">
            View and export caterer payouts. When a transfer needs an OTP, enter it here (or the caterer can enter it in their Wallet). Then click Complete.
          </p>
        </div>
        <button
          onClick={handleExport}
          className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm font-medium text-foreground shadow-sm hover:bg-muted"
        >
          <Icon name="Download" size={16} /> Export
        </button>
      </header>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
          <p className="text-sm font-medium text-muted-foreground">Total payouts</p>
          <p className="mt-1 text-2xl font-semibold text-foreground">{payouts.length}</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
          <p className="text-sm font-medium text-muted-foreground">Total amount</p>
          <p className="mt-1 text-2xl font-semibold text-foreground">R{totalAmount.toLocaleString()}</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
          <p className="text-sm font-medium text-muted-foreground">Completed</p>
          <p className="mt-1 text-2xl font-semibold text-emerald-600">{completedCount}</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
          <p className="text-sm font-medium text-muted-foreground">Failed</p>
          <p className="mt-1 text-2xl font-semibold text-rose-600">{failedCount}</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-border pb-4">
        {statusFilters.map((f) => (
          <button
            key={f.value}
            onClick={() => setStatusFilter(f.value)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium ${
              statusFilter === f.value ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead className="bg-muted/30 text-muted-foreground">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">ID</th>
                <th className="px-4 py-3 text-left font-semibold">Caterer</th>
                <th className="px-4 py-3 text-left font-semibold">Bank verified</th>
                <th className="px-4 py-3 text-left font-semibold">Amount</th>
                <th className="px-4 py-3 text-left font-semibold">Status</th>
                <th className="px-4 py-3 text-left font-semibold">Method</th>
                <th className="px-4 py-3 text-left font-semibold">Requested</th>
                <th className="px-4 py-3 text-left font-semibold">Completed</th>
                <th className="px-4 py-3 text-left font-semibold">Failure reason</th>
                <th className="px-4 py-3 text-left font-semibold">OTP</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/80">
              {loading && (
                <tr>
                  <td colSpan={10} className="px-6 py-8 text-center text-muted-foreground">Loading…</td>
                </tr>
              )}
              {!loading && !payouts.length && (
                <tr>
                  <td colSpan={10} className="px-6 py-8 text-center text-muted-foreground">No payouts found.</td>
                </tr>
              )}
              {!loading && payouts.map((p) => (
                <tr key={p.id} className="hover:bg-muted/30">
                  <td className="px-4 py-3 font-mono text-xs text-foreground">{String(p.id).slice(0, 8)}…</td>
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-foreground">{p.caterer?.business_name ?? '—'}</p>
                      <p className="text-xs text-muted-foreground">{p.caterer?.email ?? ''}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {p.caterer?.bank_verified ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">Verified</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">Not verified</span>
                    )}
                    {p.caterer?.recipient_code && (
                      <p className="mt-0.5 font-mono text-xs text-muted-foreground" title="Paystack subaccount">RCP_{String(p.caterer.recipient_code).slice(0, 8)}…</p>
                    )}
                  </td>
                  <td className="px-4 py-3 font-semibold text-foreground">R{Number(p.amount ?? 0).toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${statusStyles[p.status] ?? 'bg-muted text-foreground'}`}>
                      {p.status ?? '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground capitalize">{p.payout_method ?? '—'}</td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {p.requested_date ? format(new Date(p.requested_date), 'dd MMM yyyy HH:mm') : '—'}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {(p.completed_at || p.completed_date) ? format(new Date(p.completed_at || p.completed_date), 'dd MMM yyyy HH:mm') : '—'}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground max-w-[200px] truncate" title={p.failed_reason ?? p.failure_reason ?? ''}>
                    {p.failed_reason ?? p.failure_reason ?? '—'}
                  </td>
                  <td className="px-4 py-3">
                    {p.status === 'processing' && (p.transfer_code || p.metadata?.paystack_transfer_code) ? (
                      <div className="flex flex-wrap items-center gap-2">
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={6}
                          placeholder="OTP"
                          value={otpByPayout[p.id] ?? ''}
                          onChange={(e) => setOtpByPayout((prev) => ({ ...prev, [p.id]: e.target.value.replace(/\D/g, '') }))}
                          className="w-20 rounded border border-border bg-background px-2 py-1 text-sm font-mono"
                        />
                        <button
                          type="button"
                          disabled={finalizingId === p.id || !(otpByPayout[p.id]?.trim().length >= 4)}
                          onClick={() => handleFinalizeOtp(p.id)}
                          className="rounded bg-primary px-2 py-1 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
                        >
                          {finalizingId === p.id ? '…' : 'Complete'}
                        </button>
                        <span className="text-xs text-muted-foreground">OTP from email (platform or caterer)</span>
                      </div>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
